// src/components/ServicesGrid.js
// Preserves web exactly; only increases the icon box size (+75%) on native (is-mobile-app).
// Layout: web uses the original wrapped grid; native uses a horizontal scroller (no wrapping).
import React, { useMemo } from "react";
import ImageWithFallback from "./ImageWithFallback";

const PUBLIC_NO_IMAGE = (process.env.PUBLIC_URL || "") + "/no-image.png";

function normalizeName(s) {
  if (!s) return "";
  return String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
}

function loadServiceImages() {
  const map = {};
  try {
    // webpack / CRA require.context
    // eslint-disable-next-line global-require
    const ctx = require.context("../assets/images/Servicios", false, /\.(png|jpe?g|svg|webp)$/);
    ctx.keys().forEach((k) => {
      const fileName = k.replace("./", "");
      const key = normalizeName(fileName.replace(/\.(png|jpe?g|svg|webp)$/i, ""));
      const resolved = ctx(k);
      map[key] = resolved && resolved.default ? resolved.default : resolved;
    });
    return map;
  } catch (e) {}
  try {
    // Vite import.meta.globEager
    if (typeof import.meta !== "undefined" && typeof import.meta.globEager === "function") {
      const modules = import.meta.globEager("/src/assets/images/Servicios/*.{png,jpg,jpeg,svg,webp}");
      Object.keys(modules).forEach((p) => {
        const seg = p.split("/").pop();
        const key = normalizeName(seg.replace(/\.(png|jpe?g|svg|webp)$/i, ""));
        const mod = modules[p];
        map[key] = mod && mod.default ? mod.default : mod;
      });
      return map;
    }
  } catch (e) {}
  return map;
}

const imagesMap = loadServiceImages();

function isBrowserWeb() {
  try {
    return typeof window !== "undefined" && window.location && (window.location.protocol === "http:" || window.location.protocol === "https:");
  } catch (e) {
    return false;
  }
}

function buildRuntimeVariants(resolved) {
  const PUBLIC_NO_IMAGE_LOCAL = PUBLIC_NO_IMAGE;
  if (!resolved) return [PUBLIC_NO_IMAGE_LOCAL];
  const r = String(resolved);
  const out = [];
  out.push(r);
  // web absolute for root-relative
  if (isBrowserWeb() && r.startsWith("/")) {
    try { out.push(window.location.origin + r); } catch (e) { /* ignore */ }
  }
  try {
    const pub = (process.env.PUBLIC_URL || "");
    if (pub) {
      const cleanPub = pub.endsWith("/") ? pub.slice(0, -1) : pub;
      const stripped = r.replace(new RegExp('^/+'), "");
      out.push(cleanPub + "/" + stripped);
    }
  } catch (e) {}
  // file / webview candidates (when not web)
  if (!isBrowserWeb()) {
    const stripped = r.replace(new RegExp('^/+'), "");
    try {
      if (typeof window !== "undefined" && window.location && window.location.href && window.location.protocol === "file:") {
        const href = String(window.location.href || "");
        const idx = href.lastIndexOf("/");
        const base = idx >= 0 ? href.slice(0, idx + 1) : href;
        out.push(base + stripped);
      }
    } catch (e) {}
    out.push("file:///android_asset/public/" + stripped);
    out.push("file:///android_asset/www/" + stripped);
    out.push(stripped);
  }
  if (!out.includes(PUBLIC_NO_IMAGE_LOCAL)) out.push(PUBLIC_NO_IMAGE_LOCAL);
  return Array.from(new Set(out.filter(Boolean)));
}

function tryFindImageResolved(item, idx) {
  if (!item) return null;
  if (item.imageUrl) return item.imageUrl;
  if (item.icon) return item.icon;
  if (item.imageName) {
    const k = normalizeName(item.imageName);
    if (imagesMap[k]) return imagesMap[k];
    return (process.env.PUBLIC_URL || "") + "/assets/images/Servicios/" + item.imageName;
  }
  const raw = item.name || item.id || `service_${idx + 1}`;
  const norm = normalizeName(raw);
  if (imagesMap[norm]) return imagesMap[norm];
  const byIdxKey = `servicio${idx + 1}`;
  if (imagesMap[byIdxKey]) return imagesMap[byIdxKey];
  const foundKey = Object.keys(imagesMap).find(k => {
    if (!k) return false;
    return k.includes(norm) || norm.includes(k);
  });
  if (foundKey) return imagesMap[foundKey];
  const vals = Object.values(imagesMap || {});
  if (vals && vals.length) return vals[idx % vals.length];
  return "/static/media/" + norm + ".png";
}

/**
 * ServicesGrid
 * - Web branch: returns original wrapped grid (no changes).
 * - Native branch: returns horizontal scroller; only the image box size is increased by 75% (visual).
 */
export default function ServicesGrid({ businessTypes = [], onSelectCategory }) {
  const isMobileApp = (typeof document !== "undefined" && document.body && document.body.classList && document.body.classList.contains("is-mobile-app"));

  const list = useMemo(() => {
    const seen = new Set();
    const out = [];
    (Array.isArray(businessTypes) ? businessTypes : []).forEach((b, idx) => {
      if (!b) return;
      const id = b.id || "";
      const nameKey = normalizeName(b.name || id || `service_${idx}`);
      const key = id ? `id:${id}` : `name:${nameKey}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(b);
    });
    return out;
  }, [businessTypes]);

  // --- WEB: exact original layout (wrap grid) ---
  if (!isMobileApp) {
    const CARD_WIDTH = 154;
    const IMAGE_BOX = 118;
    return (
      <section className="max-w-6xl mx-auto px-6 py-4">
        <div className="services-default-grid">
          {list && list.length > 0 ? (
            list.map((cat, idx) => {
              const labelText = cat && (cat.name || cat.id) ? cat.name || cat.id : "Sin nombre";
              const resolved = tryFindImageResolved(cat, idx) || PUBLIC_NO_IMAGE;
              let primary = resolved;
              if (isBrowserWeb() && typeof resolved === "string" && resolved.startsWith("/")) {
                try { primary = window.location.origin + resolved; } catch (e) { primary = resolved; }
              }
              const candidates = buildRuntimeVariants(resolved);
              const ordered = Array.from(new Set([primary].concat(candidates)));
              const key = cat.id || `${normalizeName(labelText)}_${idx}`;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSelectCategory && onSelectCategory(cat)}
                  aria-label={`Servicio ${labelText}`}
                  style={{
                    width: `${CARD_WIDTH}px`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <div
                    className="service-image-box"
                    style={{
                      width: IMAGE_BOX,
                      height: IMAGE_BOX,
                      borderRadius: 16,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      background: "transparent",
                    }}
                  >
                    <ImageWithFallback
                      key={ordered[0]}
                      src={ordered[0]}
                      candidates={ordered.slice(1)}
                      alt={labelText}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      fallback={PUBLIC_NO_IMAGE}
                    />
                  </div>

                  <div className="service-label" style={{ marginTop: 6, textAlign: "center", fontSize: 13 }}>
                    {labelText}
                  </div>
                </button>
              );
            })
          ) : (
            <div className="col-span-full text-slate-500">No hay servicios disponibles.</div>
          )}
        </div>
      </section>
    );
  }

  // --- NATIVE: horizontal scroller + larger images (only visual change) ---
  const SCALE_ON_MOBILE = 1;
  const BASE_IMAGE_BOX = 82;
  const IMAGE_BOX_SIZE = Math.round(BASE_IMAGE_BOX * SCALE_ON_MOBILE);
  const CARD_WIDTH_NATIVE = 112;

  return (
    <section className="max-w-6xl mx-auto px-6 py-4">
      <div className="services-default-grid">
        {list && list.length > 0 ? (
          <div
            className="services-horizontal-scroll"
            style={{
              display: 'flex',
              gap: 16,
              overflowX: 'auto',
              padding: '8px 4px',
              WebkitOverflowScrolling: 'touch',
              flexWrap: 'nowrap'
            }}
          >
            {list.map((cat, idx) => {
              const labelText = cat && (cat.name || cat.id) ? cat.name || cat.id : "Sin nombre";
              const resolved = tryFindImageResolved(cat, idx) || PUBLIC_NO_IMAGE;
              const candidates = buildRuntimeVariants(resolved);
              const primary = (isBrowserWeb() && typeof resolved === "string" && resolved.startsWith("/")) ? (window.location.origin + resolved) : resolved;
              const ordered = Array.from(new Set([primary].concat(candidates)));
              const key = cat.id || `${normalizeName(labelText)}_${idx}`;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onSelectCategory && onSelectCategory(cat)}
                  aria-label={`Servicio ${labelText}`}
                  style={{
                    width: `${CARD_WIDTH_NATIVE}px`,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    background: "transparent",
                    border: "none",
                    padding: '8px 4px',
                    cursor: "pointer",
                    flex: '0 0 auto'
                  }}
                >
                  <div
                    className="service-image-box"
                    style={{
                      width: IMAGE_BOX_SIZE,
                      height: IMAGE_BOX_SIZE,
                      borderRadius: 16,
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      background: "transparent",
                    }}
                  >
                    <ImageWithFallback
                      key={ordered[0]}
                      src={ordered[0]}
                      candidates={ordered.slice(1)}
                      alt={labelText}
                      style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
                      fallback={PUBLIC_NO_IMAGE}
                    />
                  </div>

                  <div className="service-label" style={{ marginTop: 6, textAlign: "center", fontSize: 13 }}>
                    {labelText}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="col-span-full text-slate-500">No hay servicios disponibles.</div>
        )}
      </div>
    </section>
  );
}